import { FormattedText } from "@tm/static";
import { theme } from "@tm/utility/global";
import React, { useCallback, useMemo } from "react";

import { Card, CardPadding } from "../../Claims.style";
import { ModalActionId, useClaimModal } from "../../modalManager";
import type {
  ClaimedCard,
  ClaimedCardAction,
  ClaimStatusCardProps,
} from "../../types";
import {
  ActionButton,
  CardList,
  ClaimedCardContent,
  ClaimedCardTitle,
  ClaimedCardWrapper,
  ClaimTypeRow,
  ClaimTypeTitle,
  GradientBar,
  GradientBarContainer,
  GradientBarTrack,
  NoClaimsText,
  SectionTitle,
} from "./ClaimStatusCard.style";

function resolveGradientColors(raw: string | string[] | undefined): string[] {
  if (Array.isArray(raw) && raw.length >= 2) return raw;
  if (typeof raw === "string") return [raw, raw];
  return [theme.lightBlack, theme.lightBlack];
}

// ── Action hook (mirrors RN useActionHandler) ───────────────────────────────

function useActionHandler() {
  const { openModal } = useClaimModal();

  return useCallback(
    (action: ClaimedCardAction) => {
      const { actionId } = action;

      if (
        actionId === ModalActionId.VIEW_TRAVELLER_DETAIL &&
        action.viewDetailData
      ) {
        openModal(ModalActionId.VIEW_TRAVELLER_DETAIL, action.viewDetailData);
        return;
      }

      if (
        actionId === ModalActionId.ADD_BANK_DETAIL &&
        action.addBankDetailData
      ) {
        openModal(ModalActionId.ADD_BANK_DETAIL, {
          data: {
            title: action.addBankDetailData.title,
            fields: action.addBankDetailData.fields,
            submitButton: action.addBankDetailData.submitButton,
          },
        });
      }
    },
    [openModal]
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

interface GradientBottomBarProps {
  colors: string[];
}

function GradientBottomBar({ colors }: GradientBottomBarProps) {
  return (
    <GradientBarContainer>
      <GradientBarTrack />
      <GradientBar $colors={colors} />
    </GradientBarContainer>
  );
}

interface ClaimedCardItemProps {
  card: ClaimedCard;
}

function ClaimedCardItem({ card }: ClaimedCardItemProps) {
  const { style, title, claimTypeTitle, action } = card;
  const handleAction = useActionHandler();

  const gradientColors = useMemo(
    () => resolveGradientColors(style?.bottomBorderColor),
    [style?.bottomBorderColor]
  );

  const actionTitle = action?.title?.text?.value;
  const hasVisibleAction = Boolean(actionTitle);

  const onActionPress = useCallback(() => {
    if (action) {
      handleAction(action);
    }
  }, [action, handleAction]);

  return (
    <ClaimedCardWrapper>
      <ClaimedCardContent>
        {title ? (
          <ClaimedCardTitle $color={style?.titleColor}>
            <FormattedText value={title} />
          </ClaimedCardTitle>
        ) : null}

        <ClaimTypeRow>
          {claimTypeTitle ? (
            <ClaimTypeTitle $color={style?.claimTypeTitleColor}>
              <FormattedText value={claimTypeTitle} />
            </ClaimTypeTitle>
          ) : null}

          {hasVisibleAction ? (
            <ActionButton onClick={onActionPress} aria-label={actionTitle}>
              {actionTitle}
            </ActionButton>
          ) : null}
        </ClaimTypeRow>
      </ClaimedCardContent>

      <GradientBottomBar colors={gradientColors} />
    </ClaimedCardWrapper>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

export const ClaimStatusCard: React.FC<ClaimStatusCardProps> = ({
  title,
  noClaimsTitle,
  claimedCards,
}) => {
  const hasClaims = Array.isArray(claimedCards) && claimedCards.length > 0;

  return (
    <Card>
      <CardPadding>
        {title?.text?.value ? (
          <SectionTitle>
            <FormattedText value={title} />
          </SectionTitle>
        ) : null}

        {!hasClaims && noClaimsTitle?.text?.value ? (
          <NoClaimsText>
            <FormattedText value={noClaimsTitle} />
          </NoClaimsText>
        ) : null}

        {hasClaims ? (
          <CardList>
            {claimedCards?.map((card, index) => (
              <ClaimedCardItem key={card.state ?? index} card={card} />
            ))}
          </CardList>
        ) : null}
      </CardPadding>
    </Card>
  );
};
